import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNotificationBroadcasts, useNotificationTemplates } from "services/hooks/rentals";
import { BROADCAST_AUDIENCE_OPTIONS } from "../constants";

const audienceToEventKey = {
  TENANTS: "tenant_broadcast",
  LANDLORDS: "landlord_broadcast",
};

const BroadcastsTab = () => {
  const {
    broadcasts,
    isLoadingBroadcasts,
    broadcastsError,
    isSubmittingBroadcast,
    isProcessingBroadcast,
    broadcastActionError,
    createBroadcast,
    processBroadcast,
    setNewData,
  } = useNotificationBroadcasts();
  const { templates, isLoadingTemplates } = useNotificationTemplates();

  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    audience: "TENANTS",
    related_template: "",
    dry_run: false,
  });

  const broadcastTemplates = useMemo(() => {
    return templates.filter((template) => {
      if (!template.is_active) return false;
      const eventKey = template.event_key;
      return eventKey === "tenant_broadcast" || eventKey === "landlord_broadcast";
    });
  }, [templates]);

  const templateOptionsForAudience = useMemo(() => {
    const requiredEventKey = audienceToEventKey[form.audience];
    return broadcastTemplates.filter((template) => template.event_key === requiredEventKey);
  }, [broadcastTemplates, form.audience]);

  const handleOpenCreate = () => {
    setForm({
      name: "",
      audience: "TENANTS",
      related_template: "",
      dry_run: false,
    });
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setMessage({ type: "error", text: "Job name is required." });
      return;
    }
    if (!form.related_template) {
      setMessage({ type: "error", text: "Select a broadcast template." });
      return;
    }
    const payload = {
      name: form.name.trim(),
      audience: form.audience,
      related_template: Number(form.related_template),
      dry_run: Boolean(form.dry_run),
    };
    const response = await createBroadcast(payload);
    if (response.success) {
      setMessage({
        type: "success",
        text: form.dry_run ? "Dry-run broadcast job created." : "Broadcast job created. Run Process when ready.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to create broadcast job." });
    }
  };

  const handleProcess = async (job) => {
    const label = job?.id ? `broadcast job #${job.id}` : "this job";
    if (!window.confirm(`Process ${label}? This will send messages to the audience.`)) return;
    const response = await processBroadcast(job.id);
    if (response.success) {
      setMessage({ type: "success", text: `Broadcast job #${job.id} processed.` });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to process broadcast job." });
    }
  };

  const resolveTemplateLabel = (row) => {
    const template = row.related_template;
    if (template && typeof template === "object") {
      return template.name || `Template #${template.id}`;
    }
    const match = broadcastTemplates.find((item) => item.id === row.related_template);
    return match?.name || (row.related_template ? `Template #${row.related_template}` : "—");
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Broadcast Jobs</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={() => setNewData(true)} disabled={isLoadingBroadcasts}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create Job
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create a job with audience TENANTS or LANDLORDS and an event template (
        <code>tenant_broadcast</code> / <code>landlord_broadcast</code>), then process it to send.
      </Typography>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}
      {broadcastsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load broadcast jobs.
        </Alert>
      )}
      {broadcastActionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {broadcastActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingBroadcasts ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Dry run</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {broadcasts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>No broadcast jobs yet.</TableCell>
                </TableRow>
              ) : (
                broadcasts.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{row.audience || "—"}</TableCell>
                    <TableCell>{resolveTemplateLabel(row)}</TableCell>
                    <TableCell>{row.status || "—"}</TableCell>
                    <TableCell>{row.dry_run ? "Yes" : "No"}</TableCell>
                    <TableCell>{row.date_created || row.created_at || "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        title="Process job"
                        onClick={() => handleProcess(row)}
                        disabled={isProcessingBroadcast}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Broadcast Job</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Job name"
            required
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            helperText="Required — e.g. Send tenant references May 2026"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Audience</InputLabel>
            <Select
              label="Audience"
              value={form.audience}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  audience: event.target.value,
                  related_template: "",
                }))
              }
            >
              {BROADCAST_AUDIENCE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={form.related_template}
              onChange={(event) => setForm((prev) => ({ ...prev, related_template: event.target.value }))}
              disabled={isLoadingTemplates}
            >
              {templateOptionsForAudience.length === 0 ? (
                <MenuItem value="" disabled>
                  No active {audienceToEventKey[form.audience]} templates — create one under Event Templates
                </MenuItem>
              ) : (
                templateOptionsForAudience.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} ({template.channel})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.dry_run}
                onChange={(event) => setForm((prev) => ({ ...prev, dry_run: event.target.checked }))}
              />
            }
            label="Dry run (no sends)"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={isSubmittingBroadcast}>
            {isSubmittingBroadcast ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastsTab;
